import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  adminEnrichBulk,
  adminEnrichBulkStatus,
  adminEnrichBulkCancel,
  adminBackfillSocial,
  adminBackfillSocialStatus,
  adminBackfillSocialCancel,
  adminEnrichQueue,
  adminEnrichTool,
  adminRecomputeAlternatives,
  adminRecomputeAlternativesStatus,
  adminRecomputeAlternativesCancel,
  adminSeedTerminals,
  adminUpdate,
  adminToolHandles,
} from "../lib/api";

const STATUS_TABS = ["draft", "none", "approved", "all"];

export default function AdminEnrich({ focus = null, userBases = [], workflowStages = [] }) {
  // categories endpoint may not have been threaded for workflow_stages here;
  // fall back to a hardcoded canonical order matching the backend.
  const WF = workflowStages.length
    ? workflowStages
    : ["Idea Generation", "Screening", "Research", "Valuation", "Monitoring", "Execution"];

  const [statusTab, setStatusTab] = useState(() => (focus && focus.slug ? "none" : "draft"));
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ none: 0, draft: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [bulk, setBulk] = useState(null);
  const pollRef = useRef(null);
  const [rel, setRel] = useState(null);
  const relPollRef = useRef(null);
  const [social, setSocial] = useState(null);
  const socialPollRef = useRef(null);
  const [hm, setHm] = useState(null);
  const [hmQ, setHmQ] = useState("");
  const [hmMissing, setHmMissing] = useState(true);
  const [hmEdits, setHmEdits] = useState({});
  const [hmSaving, setHmSaving] = useState("");
  const [focusSlug, setFocusSlug] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const runSeedTerminals = async () => {
    if (seeding) return;
    setSeeding(true);
    try {
      const r = await adminSeedTerminals();
      setSeedResult(r);
      toast.success(`tagged ${r.tagged_count} terminals (${r.skipped_already_set} already set)`);
      load();
    } catch (e) {
      toast.error("could not seed terminals");
    } finally {
      setSeeding(false);
    }
  };

  // Parent (the "[draft]" badge in the submissions table) can request focus on
  // a specific tool. Jump to the "none" (un-enriched) queue and remember the
  // slug so we scroll/highlight once rows load. A fresh {slug, nonce} object is
  // sent on each click, so this re-runs every time. Deferred to a microtask to
  // keep setState out of the synchronous effect body.
  useEffect(() => {
    if (!focus || !focus.slug) return undefined;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (cancelled) return;
      setFocusSlug(focus.slug);
      setStatusTab("none");
    });
    return () => {
      cancelled = true;
    };
  }, [focus]);

  // After the rows for the focused tool load, scroll to it and flash a highlight.
  useEffect(() => {
    if (!focusSlug || loading || !rows.some((t) => t.slug === focusSlug)) return;
    const el = document.querySelector(`[data-testid="enrich-row-${focusSlug}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const timer = setTimeout(() => setFocusSlug(null), 4000);
    return () => clearTimeout(timer);
  }, [rows, loading, focusSlug]);

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const data = await adminEnrichQueue(statusTab);
      if (seq !== loadSeq.current) return; // a newer load started — ignore stale response
      setRows(data.tools || []);
      setCounts(data.counts || {});
    } catch (e) {
      if (seq === loadSeq.current) toast.error("could not load enrichment queue");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [statusTab]);

  useEffect(() => {
    // Defer load() to a microtask so its initial setLoading(true) isn't a
    // synchronous state update inside the effect body.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) load();
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Poll bulk status while a run is in flight.
  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await adminEnrichBulkStatus();
        setBulk(s);
        if (!s.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          load();
        }
      } catch {
        /* ignore */
      }
    }, 2500);
  }, [load]);

  useEffect(() => {
    // Resume polling if a run is already going when the tab opens.
    adminEnrichBulkStatus().then((s) => {
      setBulk(s);
      if (s.running) startPolling();
    }).catch(() => {});
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [startPolling]);

  const runBulk = async (force) => {
    try {
      const res = await adminEnrichBulk({ force, limit: 5000 });
      if (res.started) {
        toast.success("enrichment run started");
        startPolling();
      } else {
        toast.message(res.reason || "already running");
        startPolling();
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "could not start run");
    }
  };

  // Recompute AI "alternatives" across the catalog + symmetrize.
  const startRelPolling = useCallback(() => {
    if (relPollRef.current) clearInterval(relPollRef.current);
    relPollRef.current = setInterval(async () => {
      try {
        const s = await adminRecomputeAlternativesStatus();
        setRel(s);
        if (!s.running) {
          clearInterval(relPollRef.current);
          relPollRef.current = null;
          load();
        }
      } catch {
        /* ignore */
      }
    }, 2500);
  }, [load]);

  useEffect(() => {
    adminRecomputeAlternativesStatus().then((s) => {
      setRel(s);
      if (s.running) startRelPolling();
    }).catch(() => {});
    return () => relPollRef.current && clearInterval(relPollRef.current);
  }, [startRelPolling]);

  const runRecomputeAlts = async () => {
    try {
      const res = await adminRecomputeAlternatives();
      if (res.started) toast.success("recomputing alternatives…");
      else toast.message(res.reason || "already running");
      startRelPolling();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "could not start recompute");
    }
  };

  // Backfill social handles (deterministic, no LLM).
  const startSocialPolling = useCallback(() => {
    if (socialPollRef.current) clearInterval(socialPollRef.current);
    socialPollRef.current = setInterval(async () => {
      try {
        const s = await adminBackfillSocialStatus();
        setSocial(s);
        if (!s.running) {
          clearInterval(socialPollRef.current);
          socialPollRef.current = null;
        }
      } catch {
        /* ignore */
      }
    }, 2500);
  }, []);

  useEffect(() => {
    adminBackfillSocialStatus().then((s) => {
      setSocial(s);
      if (s.running) startSocialPolling();
    }).catch(() => {});
    return () => socialPollRef.current && clearInterval(socialPollRef.current);
  }, [startSocialPolling]);

  const runBackfillSocial = async (onlyMissing) => {
    try {
      const res = await adminBackfillSocial({ onlyMissing });
      if (res.started) toast.success("grabbing X handles…");
      else toast.message(res.reason || "already running");
      startSocialPolling();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "could not start backfill");
    }
  };

  const stopBackfillSocial = async () => {
    try {
      const res = await adminBackfillSocialCancel();
      toast.message(res.ok ? "stopping after the current tool…" : (res.reason || "not running"));
      if (res.status) setSocial(res.status);
    } catch {
      toast.error("could not stop the backfill");
    }
  };

  // Manual X-handle manager — load + inline-save handles to grow the daily-post pool.
  const loadHandles = async (q = hmQ, onlyMissing = hmMissing) => {
    try {
      const data = await adminToolHandles(q, onlyMissing);
      setHm(data);
      setHmEdits({});
    } catch {
      toast.error("could not load tool handles");
    }
  };

  const saveHandle = async (t) => {
    const val = (hmEdits[t.id] ?? t.x ?? t.suggested ?? "").trim().replace(/^@/, "");
    setHmSaving(t.id);
    try {
      await adminUpdate(t.id, { x_handle: val });
      toast.success(val ? `@${val} → ${t.title}` : `cleared ${t.title}`);
      setHm((s) => {
        if (!s) return s;
        let tools = s.tools.map((x) => (x.id === t.id ? { ...x, x: val } : x));
        if (hmMissing && val) tools = tools.filter((x) => x.id !== t.id);
        return { ...s, tools, with_handle: s.with_handle + (val && !t.x ? 1 : 0) };
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not save handle");
    } finally {
      setHmSaving("");
    }
  };

  const bulkSaveSuggestions = async () => {
    if (!hm) return;
    const targets = hm.tools.filter(
      (t) => !t.x && ((hmEdits[t.id] ?? t.suggested) || "").trim()
    );
    if (!targets.length) {
      toast.message("no suggestions to fill");
      return;
    }
    setHmSaving("__bulk__");
    let ok = 0;
    for (const t of targets) {
      const val = (hmEdits[t.id] ?? t.suggested ?? "").trim().replace(/^@/, "");
      try {
        await adminUpdate(t.id, { x_handle: val });
        ok += 1;
      } catch {
        /* skip failures (e.g. invalid guess), keep going */
      }
    }
    setHmSaving("");
    toast.success(`saved ${ok} handle${ok === 1 ? "" : "s"} — review the rest`);
    loadHandles();
  };

  const stopBulk = async () => {
    try {
      const res = await adminEnrichBulkCancel();
      toast.message(res.ok ? "stopping after the current tool…" : (res.reason || "not running"));
      if (res.status) setBulk(res.status);
    } catch {
      toast.error("could not stop the run");
    }
  };

  const stopRecomputeAlts = async () => {
    try {
      const res = await adminRecomputeAlternativesCancel();
      toast.message(res.ok ? "stopping after the current tool…" : (res.reason || "not running"));
      if (res.status) setRel(res.status);
    } catch {
      toast.error("could not stop the recompute");
    }
  };

  return (
    <div data-testid="admin-enrich">
      {/* Bulk runner */}
      <div className="border border-black p-3 mb-4">
        <div className="text-[13px] font-bold mb-1">AI enrichment — draft site descriptions</div>
        <p className="text-[12px] text-[#666] mb-2">
          Drafts overview / best-for / workflow position / user base / pricing for every
          tool (skips already-enriched). Nothing is public until you{" "}
          <span className="font-bold">approve</span> each one below.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button className="tv-btn tv-btn-primary" onClick={() => runBulk(false)} data-testid="enrich-run-missing">
            [draft missing]
          </button>
          <button className="tv-btn" onClick={() => runBulk(true)} data-testid="enrich-run-force">
            [re-draft ALL (force)]
          </button>
          {bulk?.running && (
            <button className="tv-btn" onClick={stopBulk} data-testid="enrich-stop">
              [■ stop]
            </button>
          )}
          <button className="tv-btn" onClick={load} data-testid="enrich-refresh">[refresh]</button>
        </div>
        {bulk && (bulk.running || bulk.processed > 0) && (
          <div className="mt-2 text-[12px] text-[#444]" data-testid="enrich-bulk-status">
            {bulk.running ? "running… " : (bulk.cancelled ? "stopped. " : "done. ")}
            {bulk.processed}/{bulk.total} processed · ok {bulk.ok} · failed {bulk.failed} · skipped {bulk.skipped}
            {bulk.current ? ` · current: ${bulk.current}` : ""}
            {bulk.error ? ` · error: ${bulk.error}` : ""}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-[#eee]">
          <div className="text-[12px] text-[#666] mb-2">
            Grab <span className="font-bold">X / social handles</span> for existing tools by
            re-scraping each site&apos;s footer/header (deterministic — no AI, no cost).
            Only fills <span className="font-bold">blank</span> handles; never overwrites one you set.
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="tv-btn tv-btn-primary" onClick={() => runBackfillSocial(true)} data-testid="backfill-social-missing">
              [grab missing X handles]
            </button>
            <button className="tv-btn" onClick={() => runBackfillSocial(false)} data-testid="backfill-social-all">
              [re-scan ALL]
            </button>
            {social?.running && (
              <button className="tv-btn" onClick={stopBackfillSocial} data-testid="backfill-social-stop">
                [■ stop]
              </button>
            )}
          </div>
          {social && (social.running || social.processed > 0) && (
            <div className="mt-2 text-[12px] text-[#444]" data-testid="backfill-social-status">
              {social.running ? "scanning… " : (social.cancel ? "stopped. " : "done. ")}
              {social.processed}/{social.total} scanned · handles added {social.updated} ·
              failed {social.failed} · skipped {social.skipped}
              {social.current ? ` · current: ${social.current}` : ""}
              {social.error ? ` · error: ${social.error}` : ""}
            </div>
          )}
          {social?.found?.length > 0 && (
            <div className="mt-1 text-[11px] text-[#444]" data-testid="backfill-social-found">
              newly tagged:{" "}
              {social.found.slice(0, 20).map((f, i) => (
                <span key={i}>
                  {i > 0 ? " · " : ""}{f.title} <span className="text-[#888]">@{f.x}</span>
                </span>
              ))}
              {social.found.length > 20 ? ` … +${social.found.length - 20} more` : ""}
            </div>
          )}

          {/* Manual X-handle manager — fill handles the auto-scrape missed, to
              widen the daily-post eligible pool. */}
          <div className="mt-3 pt-3 border-t border-dashed border-[#ddd]" data-testid="handle-manager">
            <div className="text-[12px] text-[#666] mb-2">
              <span className="font-bold">X handle manager</span> — the auto-scrape only finds
              handles it&apos;s confident about, so most sites come up blank. Add them here to grow
              the daily-post pool (the engine only features tools that have a handle).
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                value={hmQ}
                onChange={(e) => setHmQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadHandles()}
                placeholder="search tools by name…"
                className="border border-black px-2 py-1 font-mono text-[12px] bg-white"
                data-testid="handle-manager-search"
              />
              <label className="flex items-center gap-1 text-[12px] text-[#444]">
                <input
                  type="checkbox"
                  checked={hmMissing}
                  onChange={(e) => setHmMissing(e.target.checked)}
                  data-testid="handle-manager-missing-only"
                />
                only missing
              </label>
              <button className="tv-btn tv-btn-primary" onClick={() => loadHandles()} data-testid="handle-manager-load">
                [load]
              </button>
              {hm && hm.tools.some((t) => !t.x && t.suggested) && (
                <button
                  className="tv-btn"
                  onClick={bulkSaveSuggestions}
                  disabled={hmSaving === "__bulk__"}
                  title="save the domain-guessed handle for every blank tool shown (review after)"
                  data-testid="handle-manager-fill-all"
                >
                  {hmSaving === "__bulk__" ? "saving…" : "[✦ fill all suggestions]"}
                </button>
              )}
              {hm && (
                <span className="text-[12px] text-[#888]" data-testid="handle-manager-count">
                  {hm.with_handle} with handle · {hm.total} {hmMissing ? "missing" : "shown"}
                </span>
              )}
            </div>
            {hm && (
              <div className="mt-2 max-h-72 overflow-y-auto border border-[#eee]" data-testid="handle-manager-list">
                {hm.tools.length === 0 && (
                  <div className="px-2 py-2 text-[12px] text-[#888]">no tools — every tool here has a handle 🎉</div>
                )}
                {hm.tools.map((t) => (
                  <div key={`${t.id}-${t.x || t.suggested}`} className="flex items-center gap-2 px-2 py-1 border-b border-[#f0f0f0] text-[12px]">
                    <span className="flex-1 truncate" title={t.title}>{t.title}</span>
                    {!t.x && t.suggested && (
                      <span className="text-[10px] text-[#a07000]" title="guessed from the tool's domain — review before saving">guess</span>
                    )}
                    <span className="text-[#888]">@</span>
                    <input
                      type="text"
                      defaultValue={t.x || t.suggested || ""}
                      onChange={(e) => setHmEdits((s) => ({ ...s, [t.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && saveHandle(t)}
                      placeholder="handle"
                      className="border border-[#ccc] px-1 py-0.5 font-mono text-[12px] bg-white w-32"
                      data-testid={`handle-input-${t.slug}`}
                    />
                    <button
                      className="tv-action-link border border-black px-2 py-0.5"
                      onClick={() => saveHandle(t)}
                      disabled={hmSaving === t.id}
                      data-testid={`handle-save-${t.slug}`}
                    >
                      {hmSaving === t.id ? "…" : "[save]"}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-[#eee]">
          <div className="text-[12px] text-[#666] mb-2">
            Related tools: recompute <span className="font-bold">AI alternatives</span> for every
            approved tool (genuine substitutes only — not forced) and make them symmetric.
            Replaces the existing suggestions and powers the &quot;related tools&quot; rail on each page.
          </div>
          <button
            className="tv-btn"
            onClick={runRecomputeAlts}
            disabled={rel?.running}
            data-testid="recompute-alts"
          >
            {rel?.running ? "[recomputing…]" : "[recompute AI alternatives + symmetrize]"}
          </button>
          {rel?.running && (
            <button className="tv-btn ml-2" onClick={stopRecomputeAlts} data-testid="recompute-alts-stop">
              [■ stop]
            </button>
          )}
          {rel && (rel.running || rel.processed > 0) && (
            <div className="mt-2 text-[12px] text-[#444]" data-testid="recompute-alts-status">
              {rel.running ? "running… " : (rel.cancelled ? "stopped. " : "done. ")}
              {rel.processed}/{rel.total} processed · ok {rel.ok} · failed {rel.failed}
              {rel.symmetrized ? ` · symmetrized ${rel.symmetrized}` : ""}
              {rel.current ? ` · current: ${rel.current}` : ""}
              {rel.error ? ` · error: ${rel.error}` : ""}
            </div>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-[#eee]">
          <div className="text-[12px] text-[#666] mb-2">
            Terminals: one-time tag of well-known <span className="font-bold">multi-function
            terminals</span> (Koyfin, TIKR, Godel, OpenBB, …) in this database. Run once per
            environment after deploy. Idempotent &amp; non-destructive — tools you&apos;ve already
            flagged are left untouched.
          </div>
          <button
            className="tv-btn"
            onClick={runSeedTerminals}
            disabled={seeding}
            data-testid="seed-terminals"
          >
            {seeding ? "[tagging…]" : "[seed terminals]"}
          </button>
          {seedResult && (
            <div className="mt-2 text-[12px] text-[#444]" data-testid="seed-terminals-status">
              done. tagged {seedResult.tagged_count} · skipped (already set){" "}
              {seedResult.skipped_already_set}
              {Array.isArray(seedResult.tagged) && seedResult.tagged.length > 0 && (
                <span> · {seedResult.tagged.map((t) => t.title).join(", ")}</span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="enrich-status-tabs">
        {STATUS_TABS.map((s) => (
          <button
            key={s}
            className={`tv-cat-pill ${statusTab === s ? "active" : ""}`}
            onClick={() => setStatusTab(s)}
            data-testid={`enrich-tab-${s}`}
          >
            {s}{s !== "all" && counts[s] != null ? ` (${counts[s]})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[13px] text-[#888] py-6" data-testid="enrich-loading">loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-[13px] text-[#888] py-6" data-testid="enrich-empty">no tools in this state.</div>
      ) : (
        <div className="space-y-4">
          {rows.map((t) => (
            <EnrichRow key={t.id} tool={t} userBases={userBases} workflow={WF} onChanged={load} highlight={t.slug === focusSlug} />
          ))}
        </div>
      )}
    </div>
  );
}

function EnrichRow({ tool, userBases, workflow, onChanged, highlight = false }) {
  const [overview, setOverview] = useState(tool.overview || "");
  const [bestFor, setBestFor] = useState(tool.best_for || "");
  const [wf, setWf] = useState(new Set(tool.workflow_position || []));
  const [ub, setUb] = useState(new Set(tool.user_base || []));
  const [alts, setAlts] = useState((tool.alternatives || []).join(", "));
  const [paired, setPaired] = useState((tool.paired_with || []).join(", "));
  const [plans, setPlans] = useState(
    (tool.pricing_detail || []).map((p) => ({ plan: p.plan || "", price: p.price || "" })),
  );
  const [access, setAccess] = useState(tool.access || {});
  const [accessDirty, setAccessDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const setAcc = (k, v) => {
    setAccess((a) => ({ ...a, [k]: v }));
    setAccessDirty(true);
  };
  const useScrapedAccess = (a) => {
    setAccess({
      has_api: !!a.has_api, api_url: a.api_url || "",
      has_mcp: !!a.has_mcp, mcp_url: a.mcp_url || "",
      chrome_extension: !!a.chrome_extension, claude_skill: !!a.claude_skill,
    });
    setAccessDirty(true);
  };

  const toggle = (setter, set, v) => {
    const next = new Set(set);
    if (next.has(v)) next.delete(v); else next.add(v);
    setter(next);
  };

  const setPlanField = (i, key, val) =>
    setPlans((ps) => ps.map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));
  const addPlan = () => setPlans((ps) => [...ps, { plan: "", price: "" }]);
  const removePlan = (i) => setPlans((ps) => ps.filter((_, idx) => idx !== i));

  const patch = () => ({
    overview: overview.trim(),
    best_for: bestFor.trim(),
    workflow_position: Array.from(wf),
    user_base: Array.from(ub),
    alternatives: alts,
    paired_with: paired,
    pricing_detail: plans.filter((p) => p.plan.trim() || p.price.trim()),
    ...(accessDirty ? { access } : {}),
  });

  const hasContent = () =>
    overview.trim() || bestFor.trim() || wf.size || ub.size || alts.trim() || paired.trim() || plans.length;

  const save = async (approve) => {
    setBusy(true);
    try {
      const body = patch();
      if (approve) body.enriched_status = "approved";
      // A manual save with real content promotes none -> draft so the tool
      // becomes reachable in the draft swim-lane (and later approvable).
      else if (hasContent() && (tool.enriched_status || "none") === "none") {
        body.enriched_status = "draft";
      }
      await adminUpdate(tool.id, body);
      toast.success(approve ? `approved — ${tool.title} is live` : "saved draft");
      onChanged();
    } catch (e) {
      toast.error("save failed");
    } finally {
      setBusy(false);
    }
  };

  const redraft = async () => {
    setBusy(true);
    try {
      const res = await adminEnrichTool(tool.id);
      const u = res.tool || {};
      setOverview(u.overview || "");
      setBestFor(u.best_for || "");
      setWf(new Set(u.workflow_position || []));
      setUb(new Set(u.user_base || []));
      setAlts((u.alternatives || []).join(", "));
      setPaired((u.paired_with || []).join(", "));
      setPlans((u.pricing_detail || []).map((p) => ({ plan: p.plan || "", price: p.price || "" })));
      toast.success("re-drafted with AI");
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "draft failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`border p-3 transition-colors ${highlight ? "border-[#b45309] ring-2 ring-[#b45309] bg-[#fff7ed]" : "border-black"}`} data-testid={`enrich-row-${tool.slug}`}>
      <div className="flex items-baseline gap-2 flex-wrap mb-2">
        <span className="text-[14px] font-bold">{tool.title}</span>
        {tool.status === "pending" && (
          <span className="text-[10px] px-1.5 border border-[#b45309] text-[#b45309] uppercase tracking-wide" data-testid="enrich-pending-chip">
            pending
          </span>
        )}
        <span className="text-[11px] text-[#888]">{tool.category} · {tool.pricing}</span>
        <span className={`text-[11px] px-1.5 border ${tool.enriched_status === "approved" ? "border-[#0a7f00] text-[#0a7f00]" : tool.enriched_status === "draft" ? "border-[#b07b00] text-[#b07b00]" : "border-[#aaa] text-[#888]"}`}>
          {tool.enriched_status || "none"}
        </span>
        <a href={tool.url} target="_blank" rel="noopener noreferrer" className="tv-link text-[11px] ml-auto">{tool.url} ↗</a>
      </div>
      {tool.enriched_notes && (
        <div className="text-[11px] text-[#888] italic mb-2" data-testid="enrich-notes">AI notes: {tool.enriched_notes}</div>
      )}

      <Field label="overview">
        <textarea className="tv-input w-full text-[12px]" rows={3} value={overview} onChange={(e) => setOverview(e.target.value)} data-testid="enrich-overview" />
      </Field>
      <Field label="best for">
        <textarea className="tv-input w-full text-[12px]" rows={2} value={bestFor} onChange={(e) => setBestFor(e.target.value)} data-testid="enrich-best-for" />
      </Field>

      <Field label="workflow position">
        <div className="flex flex-wrap gap-1.5">
          {workflow.map((w) => (
            <button key={w} type="button" onClick={() => toggle(setWf, wf, w)} className={`tv-chip ${wf.has(w) ? "tv-chip-on" : ""}`}>
              {wf.has(w) ? "[x] " : "[ ] "}{w}
            </button>
          ))}
        </div>
      </Field>

      <Field label="user base">
        <div className="flex flex-wrap gap-1.5">
          {userBases.map((u) => (
            <button key={u} type="button" onClick={() => toggle(setUb, ub, u)} className={`tv-chip ${ub.has(u) ? "tv-chip-on" : ""}`}>
              {ub.has(u) ? "[x] " : "[ ] "}{u}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Field label="alternatives (tool slugs, comma-sep)">
          <input className="tv-input w-full text-[12px]" value={alts} onChange={(e) => setAlts(e.target.value)} data-testid="enrich-alternatives" />
        </Field>
        <Field label="frequently used with (slugs)">
          <input className="tv-input w-full text-[12px]" value={paired} onChange={(e) => setPaired(e.target.value)} data-testid="enrich-paired" />
        </Field>
      </div>

      <Field label="pricing plans (real, from the site — edit if the scrape missed any)">
        <div className="space-y-1" data-testid="enrich-pricing">
          {plans.map((p, i) => (
            <div key={i} className="flex items-center gap-1">
              <input
                className="tv-input flex-1 text-[12px]"
                placeholder="plan (e.g. Pro)"
                value={p.plan}
                onChange={(e) => setPlanField(i, "plan", e.target.value)}
                data-testid={`enrich-plan-name-${i}`}
              />
              <input
                className="tv-input w-32 text-[12px]"
                placeholder="price (e.g. $49/mo)"
                value={p.price}
                onChange={(e) => setPlanField(i, "price", e.target.value)}
                data-testid={`enrich-plan-price-${i}`}
              />
              <button type="button" className="tv-btn" onClick={() => removePlan(i)} data-testid={`enrich-plan-remove-${i}`}>[x]</button>
            </div>
          ))}
          <button type="button" className="tv-btn" onClick={addPlan} data-testid="enrich-plan-add">[+ add plan]</button>
        </div>
      </Field>

      <ScrapedPricing scraped={tool.pricing_scraped} current={plans} onUse={setPlans} />

      <Field label="agent access (verified — drives the 'build for agent' export)">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]" data-testid="enrich-access">
          {[
            ["has_api", "has API"],
            ["has_mcp", "has MCP"],
            ["chrome_extension", "chrome ext"],
            ["claude_skill", "claude skill"],
          ].map(([k, lbl]) => (
            <label key={k} className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={!!access[k]}
                onChange={(e) => setAcc(k, e.target.checked)}
                data-testid={`enrich-access-${k}`}
              />
              {lbl}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-1">
          <input
            className="tv-input text-[12px]"
            placeholder="api url (optional)"
            value={access.api_url || ""}
            onChange={(e) => setAcc("api_url", e.target.value)}
            data-testid="enrich-access-api-url"
          />
          <input
            className="tv-input text-[12px]"
            placeholder="mcp url (optional)"
            value={access.mcp_url || ""}
            onChange={(e) => setAcc("mcp_url", e.target.value)}
            data-testid="enrich-access-mcp-url"
          />
        </div>
        {access.verified_date && (
          <div className="text-[10px] text-[#999] mt-1">verified {access.verified_date}</div>
        )}
      </Field>

      <ScrapedAccess scraped={tool.access_scraped} onUse={useScrapedAccess} />

      <div className="flex flex-wrap gap-2 mt-2">
        <button className="tv-btn" disabled={busy} onClick={redraft} data-testid="enrich-redraft">[↻ re-draft w/ AI]</button>
        <button className="tv-btn" disabled={busy} onClick={() => save(false)} data-testid="enrich-save">[save draft]</button>
        <button className="tv-btn tv-btn-primary" disabled={busy} onClick={() => save(true)} data-testid="enrich-approve">[✓ approve & publish]</button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-2">
      <div className="text-[11px] uppercase tracking-wide text-[#888] mb-1">{label}</div>
      {children}
    </div>
  );
}

// Admin-only view of the latest auto-scraped pricing. NEVER overwrites the
// verified plans automatically — the admin can adopt it with one click.
function ScrapedPricing({ scraped, current, onUse }) {
  const plans = scraped?.pricing_detail || [];
  if (!Array.isArray(plans) || plans.length === 0) return null;
  let when = "";
  if (scraped?.scraped_at) {
    const d = new Date(scraped.scraped_at);
    if (!isNaN(d)) when = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  // Extra plans the verified set doesn't already list (by plan name).
  const haveNames = new Set((current || []).map((p) => (p.plan || "").trim().toLowerCase()));
  const extra = plans.filter((p) => !haveNames.has((p.plan || "").trim().toLowerCase()));
  return (
    <div className="mt-2 border border-dashed border-[#b07b00] bg-[#fffaf0] p-2" data-testid="scraped-pricing">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="text-[11px] uppercase tracking-wide text-[#b07b00]">
          auto-detected on site{when ? ` · scraped ${when}` : ""} (admin-only)
        </div>
        <button
          type="button"
          className="tv-btn text-[11px]"
          onClick={() => onUse(plans.map((p) => ({ plan: p.plan || "", price: p.price || "" })))}
          data-testid="scraped-pricing-use"
        >
          [use these]
        </button>
      </div>
      <div className="text-[12px]">
        {plans.map((p, i) => {
          const isExtra = extra.includes(p);
          return (
            <div key={i} className="flex items-baseline justify-between gap-3 py-0.5" data-testid={`scraped-plan-${i}`}>
              <span className={isExtra ? "text-[#b07b00] font-bold" : "text-[#555]"}>
                {p.plan || "—"}{isExtra ? " (new)" : ""}
              </span>
              <span className="text-[#333] whitespace-nowrap">{p.price || "—"}</span>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-[#999] mt-1">
        your verified plans above stay the source of truth — these never replace them automatically.
      </div>
    </div>
  );
}


// Admin-only view of AI-detected agent access. The model flags these only from
// explicit site evidence — admin confirms with one click (which stamps the
// verified date). Hidden when nothing was detected.
function ScrapedAccess({ scraped, onUse }) {
  if (!scraped) return null;
  const flags = [
    ["has_api", "API", scraped.api_url],
    ["has_mcp", "MCP", scraped.mcp_url],
    ["chrome_extension", "Chrome ext", null],
    ["claude_skill", "Claude skill", null],
  ].filter(([k]) => scraped[k]);
  if (flags.length === 0) return null;
  let when = "";
  if (scraped.scraped_at) {
    const d = new Date(scraped.scraped_at);
    if (!isNaN(d)) when = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }
  return (
    <div className="mt-2 border border-dashed border-[#1f6f43] bg-[#f2fbf6] p-2" data-testid="scraped-access">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <div className="text-[11px] uppercase tracking-wide text-[#1f6f43]">
          AI-detected access{when ? ` · scraped ${when}` : ""} (admin-only)
        </div>
        <button
          type="button"
          className="tv-btn text-[11px]"
          onClick={() => onUse(scraped)}
          data-testid="scraped-access-use"
        >
          [use these]
        </button>
      </div>
      <div className="text-[12px] text-[#1f6f43] flex flex-wrap gap-x-3">
        {flags.map(([k, lbl, url]) => (
          <span key={k}>
            {lbl}
            {url ? <span className="text-[#777]"> ({url})</span> : null}
          </span>
        ))}
      </div>
      <div className="text-[10px] text-[#999] mt-1">
        confirm above to publish — capabilities change, so this stamps a verified date.
      </div>
    </div>
  );
}
